// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { Check, Copy, RefreshCw, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { ModalProps } from '@/stores/use-modal-store.ts';
import { Input } from '@/components/ui/input';
import { useOrigin } from '@/hooks/use-origin';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API_BASE, INVITE_SHORT_DOMAIN } from '@/config.ts';
import { MemberRole } from '@/models';

/**
 * TODO: refetch server data to update inviteCode
 * @param param0
 * @param param0.isOpen
 * @param param0.onClose
 * @param param0.data
 * @constructor
 */
export const InviteModal: React.FC<ModalProps> = ({ isOpen, onClose, data }) => {
  const { t } = useTranslation();
  const origin = useOrigin();
  const { server, role, setServer } = data;

  const [inviteCode, setInviteCode] = useState(server?.inviteCode);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customCodeInput, setCustomCodeInput] = useState('');
  const [useSlug, setUseSlug] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const isAdmin = role === MemberRole.ADMIN; // || role === MemberRole.MODERATOR;
  const inviteUrl = `${origin}/invite/${inviteCode}`;
  const shortUrl = `${INVITE_SHORT_DOMAIN}/invite/${inviteCode}`;

  useEffect(() => {
    if (isOpen) {
      setInviteCode(server?.inviteCode);
      setCustomCodeInput('');
      setIsCustomMode(false);
      setUseSlug(false);
      setCodeError(null);
    }
  }, [isOpen, server]);

  const validateCustomCode = (code: string): boolean => {
    if (!/^[a-zA-Z]{6}$/.test(code)) {
      setCodeError(t('InviteModal.codeFormatError'));
      return false;
    }
    const uppercaseCount = (code.match(/[A-Z]/g) || []).length;
    if (uppercaseCount < 2 || uppercaseCount > 3) {
      setCodeError(t('InviteModal.codeUppercaseError'));
      return false;
    }
    setCodeError(null);
    return true;
  };

  const onCopy = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success(t('InviteModal.copied'));
    setTimeout(() => setCopied(false), 1000);
  };

  const onCopyShort = () => {
    navigator.clipboard.writeText(shortUrl);
    toast.success(t('InviteModal.copiedShort'));
  };

  const onGenerate = async () => {
    try {
      setIsLoading(true);
      setCodeError(null);

      const payload: Record<string, any> = {};
      if (isCustomMode && customCodeInput) {
        if (!validateCustomCode(customCodeInput)) {
          setIsLoading(false);
          return;
        }
        payload.customCode = customCodeInput.trim();
      } else if (useSlug) {
        payload.useSlug = true;
      }

      const res = await fetch(`${API_BASE}/api/servers/${server?.id}/invite-code`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate invite');
      }

      const updatedServer = await res.json();
      setInviteCode(updatedServer.inviteCode);

      if (setServer) {
        setServer(updatedServer);
      }

      toast.success(t('InviteModal.generated'));
      navigator.clipboard.writeText(`${origin}/invite/${updatedServer.inviteCode}`);

      setIsCustomMode(false);
      setCustomCodeInput('');
    } catch (err: any) {
      toast.error(err.message || t('InviteModal.error'));
      if (err.message?.includes('already in use')) {
        setCodeError(t('InviteModal.codeTaken'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">{t('InviteModal.title')}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Label className="text-xs font-bold uppercase">{t('InviteModal.label')}</Label>
            <div className="mt-2 flex items-center gap-x-2">
              <Input disabled={isLoading} readOnly value={inviteUrl} />
              <Button disabled={isLoading} onClick={onCopy} size="sm">
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <div className="mt-2 flex items-center gap-x-2">
              <Input
                disabled={isLoading}
                readOnly
                value={shortUrl}
                className="font-mono text-xs text-muted-foreground"
                placeholder="ccalls.site/invite/..."
              />
              <Button
                disabled={isLoading}
                onClick={onCopyShort}
                size="sm"
                variant="ghost"
                className="text-xs"
              >
                <Copy className="size-3 mr-1" />
                {t('InviteModal.copyShort')}
              </Button>
            </div>
            {isAdmin && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs font-bold uppercase flex items-center gap-2">
                    <Sparkles className="size-3" />
                    {t('InviteModal.customCode')}
                  </Label>
                  <Switch
                    checked={isCustomMode}
                    onCheckedChange={setIsCustomMode}
                    disabled={isLoading}
                  />
                </div>

                {isCustomMode ? (
                  <div className="space-y-3">
                    <div>
                      <Input
                        placeholder="ABCdef"
                        value={customCodeInput}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 6);
                          setCustomCodeInput(value);
                          if (value.length === 6) validateCustomCode(value);
                        }}
                        disabled={isLoading}
                        className={codeError ? 'border-destructive' : ''}
                        maxLength={6}
                      />
                      {codeError && <p className="text-xs text-destructive mt-1">{codeError}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('InviteModal.codeHint')}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        id="slug-toggle"
                        checked={useSlug}
                        onCheckedChange={setUseSlug}
                        disabled={isLoading}
                      />
                      <Label htmlFor="slug-toggle" className="text-xs cursor-pointer">
                        {t('InviteModal.useSlug', { name: server?.name || '' })}
                      </Label>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t('InviteModal.customCodeDisabled')}
                  </p>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className="flex justify-end gap-2">
          {isAdmin && (
            <Button
              onClick={onGenerate}
              disabled={isLoading || (isCustomMode && !customCodeInput && !useSlug)}
              variant="primary"
              size="sm"
            >
              {isLoading ? t('InviteModal.generating') : t('InviteModal.generate')}
              <RefreshCw className={`ml-2 size-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
